import React, { useCallback, useMemo } from 'react';

import { Avatar, Typography, Box, Tooltip } from '@mui/material';

import { useUserDirectory } from '@/context/UserDirectoryContext';

import type { UserDirectoryEntry } from '@/types/directory';

import styles from './UserInfo.module.scss';

export interface UserInfoProps {
  /** Display name or domain account — used for directory lookup. */
  name: string;
  /** Size variant: small (24px), medium (32px), large (80px). */
  size?: 'small' | 'medium' | 'large';
  /** Show email under name. Default: true for medium/large, false for small. */
  showEmail?: boolean;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function buildTooltipContent(name: string, entry: UserDirectoryEntry | undefined): React.ReactNode {
  if (!entry) return name;

  const lines: string[] = [`Name: ${entry.displayName}`];
  if (entry.domainAccount) lines.push(`Domain account: ${entry.domainAccount}`);
  if (entry.email) lines.push(`Email: ${entry.email}`);
  if (entry.department) lines.push(`Department: ${entry.department}`);
  if (entry.jobTitle) lines.push(`Job title: ${entry.jobTitle}`);
  if (entry.officeLocation) lines.push(`Location: ${entry.officeLocation}`);

  return (
    <span>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {line}
        </React.Fragment>
      ))}
    </span>
  );
}

const AVATAR_CLASSES: Record<string, string> = {
  small: styles.userInfoAvatarSmall,
  medium: styles.userInfoAvatarMedium,
  large: styles.userInfoAvatarLarge,
};

const NAME_CLASSES: Record<string, string> = {
  small: styles.userInfoNameSmall,
  medium: styles.userInfoName,
  large: styles.userInfoNameLarge,
};

export const UserInfo: React.FC<UserInfoProps> = React.memo(({
  name,
  size = 'small',
  showEmail = false,
}) => {
  const { getEntry, getPhotoUrl } = useUserDirectory();

  const entry = getEntry(name);
  const photoUrl = getPhotoUrl(name);
  const email = entry?.email ?? null;
  const shouldShowEmail = showEmail ?? (size !== 'small');
  const tooltipContent = useMemo(() => buildTooltipContent(name, entry), [name, entry]);

  const handleImgError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    (e.target as HTMLImageElement).style.display = 'none';
  }, []);

  const displayName = entry?.displayName ?? name;

  return (
    <Box className={styles.userInfoRoot}>
      <Avatar
        src={photoUrl ?? undefined}
        className={`${styles.userInfoAvatar} ${AVATAR_CLASSES[size]}`}
        slotProps={{ img: { onError: handleImgError } }}
      >
        {getInitials(displayName)}
      </Avatar>
      <Box className={styles.userInfoText}>
        <Tooltip title={tooltipContent} placement="top" enterDelay={500}>
          <Typography className={NAME_CLASSES[size]}>
            {entry?.displayName ?? name}
          </Typography>
        </Tooltip>
        {shouldShowEmail && email && (
          <Typography variant="inherit" className={styles.userInfoEmail}>
            {email}
          </Typography>
        )}
      </Box>
    </Box>
  );
});

UserInfo.displayName = 'UserInfo';
